import React, { PureComponent } from 'react';
import { Button } from 'semantic-ui-react';
import ImageCapture from '../../../helpers/ImageCaptureUtility';
import { setImageExifInfo, rescaleImageInModal } from '../../../helpers/ImageUtility';
import Loader from '../../../components/loader';

export interface CameraImageProps {
  imageQuality?: string;
  width?: number;
  height?: number;
  onSaveCanvas?: AnyFunc;
  onClose?: AnyFunc;
  retainImageAsAttachment?: string;
  dateStamp?: boolean;
  fontSize?: number;
  fontName?: string;
  foregroundColor?: string;
}

export default class CameraImage extends PureComponent<CameraImageProps, TypeMePlease> {
  private _isMounted: boolean;
  private imageCapture: ImageCapture;
  private videoRef: any;

  constructor(props) {
    super(props);
    this.state = { cameras: null, selectedCamera: null, cameraError: null, naturalDimensions: null, scaledDimensions: null };

    this.handleTakePicture = this.handleTakePicture.bind(this);
    this.switchCameras = this.switchCameras.bind(this);
    this.updateDimensions = this.updateDimensions.bind(this);

    this._isMounted = false;
  }

  static dataURItoBlob(dataURI) {
    // convert base64/URLEncoded data component to raw binary data held in a string
    let byteString;
    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = atob(dataURI.split(',')[1]);
    } else {
      byteString = unescape(dataURI.split(',')[1]);
    }
    // separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    return new Blob([ia], { type: mimeString });
  }

  static async hasCameras() {
    const devices = await ImageCapture.getDevices();
    try {
      //handle the warning: [Deprecation] getUserMedia() no longer works on insecure origins.
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' }, audio: false });
      // close it right away so the camera light doesn't stay stuck on
      stream.getVideoTracks().forEach((track) => track.stop());
    } catch (err) {
      return false;
    }

    return devices && devices.length > 0;
  }

  static async hasMultipleCameras() {
    const devices = await ImageCapture.getDevices();
    return devices && devices.length > 1;
  }

  componentDidMount() {
    this.imageCapture = new ImageCapture(this.videoRef, this.props.imageQuality, this.props.width, this.props.height);

    this.init();
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
    if (this.imageCapture) {
      this.imageCapture.stopCamera();
    }
  }

  async init() {
    const cameraObj = await this.imageCapture.initCamera();
    if (!this._isMounted) {
      return;
    }
    if (!this.state.cameras && !cameraObj.error) {
      if (cameraObj && cameraObj.cameras && cameraObj.cameras.length > 0) {
        this.setState(
          {
            cameras: cameraObj.cameras,
            selectedCamera: cameraObj.cameras[0].deviceId,
            naturalDimensions: {
              width: cameraObj.cameraWidth,
              height: cameraObj.cameraHeight
            }
          },
          () => {
            this.updateDimensions();
          }
        );
      } else {
        this.setState({ cameraError: 'No Cameras Available' });
      }
    } else if (cameraObj.error) {
      this.setState({ cameraError: cameraObj.error });
    }
  }

  async handleTakePicture() {
    const plainImage = await this.imageCapture.takePicture();
    const imageWithExif = await setImageExifInfo(plainImage);
    this.props.onSaveCanvas(imageWithExif);
  }

  switchCameras() {
    let nextCameraId = null;
    if (this.state.selectedCamera === this.state.cameras[0].deviceId) {
      nextCameraId = this.state.cameras[1].deviceId;
    } else {
      nextCameraId = this.state.cameras[0].deviceId;
    }

    this.imageCapture.changeDevice(nextCameraId);
    this.setState({ selectedCamera: nextCameraId });
  }

  updateDimensions() {
    if (!this.state.naturalDimensions) {
      return; //dimensions not ready
    }
    const { resizedWidth, resizedHeight } = rescaleImageInModal(
      this.state.naturalDimensions.width,
      this.state.naturalDimensions.height,
      this.videoRef.parentNode.parentNode.offsetWidth
    );

    this.setState({ scaledDimensions: { width: resizedWidth, height: resizedHeight } });
  }

  render() {
    let errorMessage = null;

    if (this.state.cameraError) {
      errorMessage = (
        <div className="camera-error">
          {this.state.cameraError}
          <br />
          <Button onClick={this.props.onClose}>Close</Button>
        </div>
      );
    }
    const scaledHeight = this.state.scaledDimensions ? this.state.scaledDimensions.height : null;
    const scaledWidth = this.state.scaledDimensions ? this.state.scaledDimensions.width : null;
    return (
      <div className="center-img">
        {errorMessage}
        {!this.state.selectedCamera ? <Loader isLoading /> : null}
        <video
          ref={(elem) => {
            this.videoRef = elem;
          }}
          autoPlay
          playsInline
          width={scaledWidth}
          height={scaledHeight}
        />
      </div>
    );
  }
}
